import {
  Children,
  ComponentPropsWithoutRef,
  isValidElement,
  MouseEventHandler,
  ReactElement,
  RefObject,
} from 'react';
import { isElement, isFragment } from 'react-is';
import { CarouselStage } from './CarouselStage';
import { ScrollToIndex } from './CarouselInterface';
import { logger } from '@tuic/logger';
import { isElementWithProps } from '../../../utils';

type StageRef = RefObject<HTMLUListElement | null>;

type ItemsChildren = ComponentPropsWithoutRef<typeof CarouselStage>['children'];

/**
 * Converts children props of CarouselItems into an array of React elements, filtering out any non-element nodes.
 */
export const getChildrenAsArrayOfElements = (
  children: ItemsChildren,
): ReactElement[] => {
  const asArray = Children.toArray(children).filter(
    (node): node is ReactElement => isValidElement(node),
  );

  if (asArray.length !== 1) {
    return asArray;
  }

  // Check if the only child is a fragment ...
  const maybeFragment = asArray[0];

  if (!isFragment(maybeFragment)) {
    return asArray;
  }
  if (!isElementWithProps(maybeFragment)) {
    return asArray;
  }

  // ... and if so, return its children instead.
  // We could make this recursive, but let's not dive too deep into the rabbit hole.
  return Children.toArray(maybeFragment.props.children).filter(
    (node): node is ReactElement => isValidElement(node),
  );
};

/**
 * Creates a function to scroll the carousel to a specific index with customizable position and scroll behavior.
 *
 * @param stageRef - Reference to the carousel container element.
 * @param lastIdx - The index of the last item in the carousel.
 * @returns A function that scrolls the carousel to a specified index.
 */
export const createToIndexScroller = (
  stageRef: StageRef,
  lastIdx: number,
): ScrollToIndex => {
  return (
    idx: number,
    position: 'start' | 'center' | 'end',
    scrollBehavior: 'auto' | 'smooth' | 'instant' = 'smooth',
  ) => {
    if (!stageRef.current) {
      logger.error('Stage reference is not available.');
      return;
    }

    // Verify that the index is within bounds & wrap around if necessary.
    if (idx < 0) {
      idx = lastIdx;
    }

    if (idx > lastIdx) {
      idx = 0;
    }

    // Get the target list item
    const target = [
      ...stageRef.current.querySelectorAll<HTMLLIElement>(':scope > li'),
    ][idx];

    if (!target) {
      logger.error(`Target not found at index ${idx}.`);
      return;
    }

    const { offsetLeft, offsetWidth } = target;
    const stageWidth = stageRef.current.offsetWidth;
    let left = offsetLeft;

    // Adjust scroll position based on the `position` argument
    if (position === 'center') {
      left = offsetLeft + offsetWidth / 2 - stageWidth / 2;
    } else if (position === 'end') {
      left = offsetLeft + offsetWidth - stageWidth;
    }

    /*
    logger.debug(
      `Scrolling to index ${idx} (position: ${position}) with left offset: ${left}`,
    );
    */

    // Scroll to the calculated position
    stageRef.current.scrollTo({
      left,
      behavior: scrollBehavior,
    });
  };
};

/**
 * Calculate the visible percentage of an item in the carousel based on its current position
 * and the scroll position of the carousel.
 */
const calculateVisiblePercentage = (
  list: HTMLUListElement,
  item: HTMLLIElement,
) => {
  // Calculate the relative position of the left point of the item within the carousel.
  let leftPoint = item.offsetLeft - list.scrollLeft;

  // Set to zero if the point is beyond the left border of the carousel.
  if (leftPoint < 0) {
    leftPoint = 0;
  }

  // Calculate the relative position of the right point of the item within the carousel.
  let rightPoint = item.offsetLeft + item.offsetWidth - list.scrollLeft;

  // Set to the width of the carousel if the point is beyond the right border of the carousel.
  if (rightPoint > list.offsetWidth) {
    rightPoint = list.offsetWidth;
  }

  // Set to zero if the point is beyond the left border of the carousel.
  if (rightPoint < 0) {
    rightPoint = 0;
  }

  // As we have the positions calculated and contained within the stage, we can calculate their distance.
  let visiblePx = rightPoint - leftPoint;

  // It shouldn't be zero, but let's be cautious.
  if (visiblePx < 0) {
    visiblePx = 0;
  }

  // Calculate the percentage of the item that is visible.
  const visiblePercentage = (visiblePx / item.offsetWidth) * 100;

  // Cap the percentage at 100% to avoid rounding errors.
  return visiblePercentage > 100 ? 100 : visiblePercentage;
};

/**
 * Retrieve the indexes of items in the carousel that are currently visible.
 */
export const getVisibleIndexes = (stageRef: StageRef): number[] => {
  // If the stage is not yet rendered, return a visible index of 0.
  if (!stageRef.current) {
    return [0];
  }

  const stage = stageRef.current;
  const stageWidth = stageRef.current.offsetWidth;

  // If the stage has no width, return a visible index of 0.
  if (stageWidth === 0) {
    return [0];
  }

  //TODO: Check if this has enough browser support: https://caniuse.com/?search=queryselector%20scope
  const percentages = [
    ...stage.querySelectorAll<HTMLLIElement>(':scope > li'),
  ].map((item, idx) => {
    return calculateVisiblePercentage(stage, item);
  });

  // Find the highest percentage and use it as the maximum, but reduce it by 1%, so we add a slight buffer.
  const maxPercentage = Math.max(...percentages) * 0.99;

  // Find the indexes of items that have a percentage equal to or higher than the maximum.
  const visibleIndexes = percentages
    .map((percentage, idx) => {
      return percentage >= maxPercentage ? idx : -1;
    })
    .filter(idx => idx >= 0);

  // If no items are visible, return a visible index of 0, otherwise return the indexes of the visible items.
  return visibleIndexes.length === 0 ? [0] : visibleIndexes;
};

/**
 * Combines multiple event handlers into a single event handler.
 */
export const combineEventHandlers = (
  events: (MouseEventHandler<HTMLUListElement> | undefined)[],
): MouseEventHandler<HTMLUListElement> => {
  const filtered = events.filter(
    (event): event is MouseEventHandler => typeof event === 'function',
  );

  return ev => {
    filtered.forEach(handler => handler(ev));
  };
};
